import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import Grid from '@material-ui/core/Grid/Grid';
import Paper from '@material-ui/core/Paper';
import Avatar from '@material-ui/core/Avatar';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import CloseIcon from '@material-ui/icons/Close';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextareaAutosize from '@material-ui/core/TextareaAutosize';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import { useAlert, types } from 'react-alert';
import { debounce } from 'lodash';
import { PageTitle } from '../../../components';
import { useClinicUser, useChat } from '../../../../model/hooks';
import { COLOR } from '../../../common/constants';
import { Job } from '../../../../model/firestore_model';
import { DateHelper } from '../../../../common/helper';
import { Utils } from '../../../../common/helper';
import { pagination } from '../../../../common/constants/Constants';
import { isNotImageFile, isOverFileSize } from '../../../common/helper';
export const ChatRoom = () => {
    var _a, _b, _c;
    const { t } = useTranslation();
    const reactAlert = useAlert();
    const { id } = useParams();
    const { jobClinic } = useClinicUser();
    const { loading, loadMessages, loadMyRoom, listenFirstMessage, sendMessage, resetUnreadData } = useChat();
    const [messages, setMessages] = useState([]);
    const [myRoom, setMyRoom] = useState();
    const [worker, setWorker] = useState();
    const [isFetching, setIsFetching] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false);
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);
    const messageListComponentRef = useRef();
    const messageListInnerComponentRefs = useRef([]);
    const fileInputRef = useRef();
    const [messageText, setMessageText] = useState('');
    const [messageImage, setMessageImage] = useState();
    const [previewUrl, setPreviewUrl] = useState('');
    const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
    // 表示用のメッセージのデータ作成
    const messageContent = (item, myRoom, worker) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
        return {
            id: item.id,
            userImage: item.userId === ((_b = (_a = myRoom) === null || _a === void 0 ? void 0 : _a.members) === null || _b === void 0 ? void 0 : _b.workerId) ? (_d = (_c = worker) === null || _c === void 0 ? void 0 : _c.image) === null || _d === void 0 ? void 0 : _d.url : (_f = (_e = jobClinic) === null || _e === void 0 ? void 0 : _e.image) === null || _f === void 0 ? void 0 : _f.url,
            userName: item.userId === ((_h = (_g = myRoom) === null || _g === void 0 ? void 0 : _g.members) === null || _h === void 0 ? void 0 : _h.workerId) ? (_j = worker) === null || _j === void 0 ? void 0 : _j.fullName() : (_k = jobClinic) === null || _k === void 0 ? void 0 : _k.clinicName,
            userType: item.userId === ((_m = (_l = myRoom) === null || _l === void 0 ? void 0 : _l.members) === null || _m === void 0 ? void 0 : _m.workerId) ? 'worker' : 'clinic',
            dateTime: DateHelper.toString(item.createdAt.toDate()),
            message: item.text,
            images: item.images,
            readMark: item.userId === ((_p = (_o = myRoom) === null || _o === void 0 ? void 0 : _o.members) === null || _p === void 0 ? void 0 : _p.clinicId) && !item.isUnread,
            detail: item,
        };
    };
    // メッセージのロード
    const loadData = async (myRoom, worker, lastDocument) => {
        const result = await loadMessages(id, lastDocument);
        return result.data.reverse().map((item) => {
            return messageContent(item, myRoom, worker);
        });
    };
    // 過去のメッセージのロード
    const onMore = async () => {
        if (worker === undefined || myRoom === undefined) {
            throw new Error(t('システムエラー'));
        }
        await Utils.delay(500);
        setIsFetching(false);
        if (messages.length > 0) {
            const lastRow = messages[0];
            const _messages = await loadData(myRoom, worker, lastRow.detail.snapshot);
            if (_messages.length < pagination.limit)
                setIsCompleted(true);
            if (_messages.length > 0) {
                setMessages(_messages.concat(messages));
                const pastFirstRef = messageListInnerComponentRefs.current[pagination.limit - 1];
                pastFirstRef.scrollIntoView({ behavior: 'auto', block: 'start' });
            }
        }
    };
    // 新着メッセージを追加する
    const insertFirstMessage = (message, myRoom, worker) => {
        setMessages((prev) => {
            var _a, _b, _c, _d;
            if (message.userId !== ((_b = (_a = myRoom) === null || _a === void 0 ? void 0 : _a.members) === null || _b === void 0 ? void 0 : _b.clinicId)) {
                resetUnreadData(myRoom);
            }
            if (prev.length === 0 || (prev.length > 0 && message.id !== prev.slice(-1)[0].id)) {
                if (message.userId !== ((_d = (_c = myRoom) === null || _c === void 0 ? void 0 : _c.members) === null || _d === void 0 ? void 0 : _d.clinicId)) {
                    reactAlert.success(t('新着メッセージが届きました。'));
                }
                return prev.concat([messageContent(message, myRoom, worker)]);
            }
            return prev;
        });
    };
    useEffect(() => {
        const f = async () => {
            var _a;
            try {
                const _myRoom = await loadMyRoom(id);
                setMyRoom(_myRoom);
                const _worker = await Job.User.get((_a = _myRoom.members) === null || _a === void 0 ? void 0 : _a.workerId);
                setWorker(_worker);
                if (_worker === undefined || _myRoom === undefined) {
                    throw new Error(t('システムエラー'));
                }
                const _messages = await loadData(_myRoom, _worker);
                setMessages(_messages);
                setIsFirstLoaded(true);
                if (messageListInnerComponentRefs.current.length === 0)
                    return;
                const lastRef = messageListInnerComponentRefs.current.slice(-1)[0];
                lastRef.scrollIntoView({ behavior: 'auto', block: 'end' });
            }
            catch (e) {
                if (e instanceof Error) {
                    reactAlert.show(e.message, { type: types.ERROR });
                }
            }
        };
        if (Utils.isNotNull(jobClinic)) {
            f();
        }
    }, [jobClinic]);
    useEffect(() => {
        if (myRoom === undefined || worker === undefined || !isFirstLoaded)
            return;
        const listener = listenFirstMessage(id, (message) => {
            insertFirstMessage(message, myRoom, worker);
        });
        return () => listener();
    }, [myRoom, worker, isFirstLoaded]);
    useEffect(() => {
        messageListComponentRef.current.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);
    const onScroll = debounce((event) => {
        if (event.target.scrollTop < 100) {
            setIsFetching(true);
        }
        else {
            setIsFetching(false);
        }
    }, 300);
    useEffect(() => {
        if (!isFetching || loading || isCompleted)
            return;
        onMore();
    }, [isFetching, loading, isCompleted]);
    const onSendMessage = async () => {
        try {
            if (myRoom === undefined) {
                throw new Error(t('システムエラー'));
            }
            await sendMessage(myRoom, messageText, messageImage);
            setMessageText('');
            setMessageImage(undefined);
            fileInputRef.current.value = '';
            reactAlert.success(t('メッセージを送信しました。'));
        }
        catch (e) {
            if (e instanceof Error) {
                reactAlert.error(e.message);
            }
        }
    };
    const onButtonClick = () => {
        fileInputRef.current.click();
    };
    // 画像の選択処理
    const onChangeFile = (e) => {
        if (e.target.files !== null) {
            if (isNotImageFile(e.target.files[0].type)) {
                reactAlert.error(t('画像のみ送信が可能です。'));
                return;
            }
            if (isOverFileSize(e.target.files[0])) {
                reactAlert.error(t('ファイルサイズは5MB以内にしてください。'));
                return;
            }
            setMessageImage(e.target.files[0]);
        }
    };
    const onClickMessageImage = (e) => {
        setPreviewUrl(e.currentTarget.src);
        setOpenPreviewDialog(true);
    };
    const onClosePreviewDialog = () => {
        setOpenPreviewDialog(false);
    };
    const isValidMessage = () => {
        return !(messageText !== '' || Utils.isNotNull(messageImage));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Dialog, { open: openPreviewDialog, onClose: onClosePreviewDialog, maxWidth: "lg" },
            React.createElement(MuiDialogTitle, { disableTypography: true },
                React.createElement(SClosePreviewTitle, null, t('プレビュー')),
                React.createElement(SClosePreviewButton, { "aria-label": "close", onClick: onClosePreviewDialog },
                    React.createElement(CloseIcon, null))),
            React.createElement(SPreviewImage, null,
                React.createElement(SPreviewImageInner, { src: previewUrl, alt: t('プレビュー画像') }))),
        React.createElement(SGrid, { container: true },
            React.createElement(SInnerGrid, { item: true, xs: 12 },
                React.createElement(SPageTitle, null,
                    React.createElement(PageTitle, { image: (_b = (_a = worker) === null || _a === void 0 ? void 0 : _a.image) === null || _b === void 0 ? void 0 : _b.url }, (_c = worker) === null || _c === void 0 ? void 0 : _c.fullName())),
                React.createElement(SPaper, { elevation: 2, ref: messageListComponentRef },
                    isFetching && !isCompleted && (React.createElement(SProgress, null,
                        React.createElement(CircularProgress, { color: "primary", size: 15 }),
                        React.createElement("span", null, t('メッセージを読み込んでいます。')))),
                    (() => {
                        if (!isFirstLoaded) {
                            return (React.createElement(SProgress, null,
                                React.createElement(CircularProgress, { color: "primary", size: 15 }),
                                React.createElement("span", null, t('メッセージを読み込んでいます。'))));
                        }
                        else if (messages.length > 0) {
                            return messages.map((obj, i) => {
                                return (React.createElement("div", { id: obj.id, key: obj.id, style: { paddingBottom: '12px' }, ref: (el) => (messageListInnerComponentRefs.current[i] = el) },
                                    React.createElement(SMessageHeaderArea, null,
                                        React.createElement(SAvatar, { src: obj.userImage, alt: t('プロフィール画像') }),
                                        React.createElement(SUserName, { "data-usertype": obj.userType }, obj.userName),
                                        React.createElement(SDateTime, null, obj.dateTime)),
                                    React.createElement(SMessage, null, obj.message),
                                    obj.images.length > 0 && (React.createElement("div", null,
                                        React.createElement(SMessageImage, { src: obj.images[0].url, alt: t('メッセージ画像'), onClick: onClickMessageImage }))),
                                    obj.readMark && React.createElement(SReadMark, null,
                                        "(",
                                        t('既読'),
                                        ")")));
                            });
                        }
                        else {
                            return React.createElement("div", null, t('メッセージはまだありません。'));
                        }
                    })()),
                React.createElement(SPaperMessage, { elevation: 2 },
                    React.createElement(SSendIconArea, null,
                        Utils.isNotNull(messageImage) && (React.createElement(SUploadImage, { src: (window.URL || window.webkitURL).createObjectURL(messageImage), alt: t('アップロード画像') })),
                        React.createElement(IconButton, { size: "small", style: { marginRight: '12px' }, onClick: onButtonClick },
                            React.createElement(Icon, null, "add_photo_alternate")),
                        (() => {
                            if (!loading) {
                                return (React.createElement(IconButton, { size: "small", onClick: onSendMessage, disabled: isValidMessage() },
                                    React.createElement(Icon, null, "send")));
                            }
                            return React.createElement(CircularProgress, { color: "primary", size: 15 });
                        })(),
                        React.createElement("input", { type: "file", ref: fileInputRef, style: { display: 'none' }, onChange: onChangeFile, accept: "image/*" })),
                    React.createElement(STextareaAutosize, { rowsMin: 3, onChange: (e) => setMessageText(e.target.value), placeholder: t('メッセージを入力する'), value: messageText }))))));
};
const SGrid = styled(Grid) `
  height: calc(100vh - 96px);
`;
const SInnerGrid = styled(Grid) `
  height: 100%;
  display: flex;
  flex-direction: column;
`;
const SPageTitle = styled.div `
  margin-bottom: 24px;
`;
const SPaper = styled(Paper) `
  padding: 24px;
  overflow: hidden scroll;
  height: 100vh;
`;
const SPaperMessage = styled(Paper) `
  padding: 8px 24px;
  margin-top: 24px;
`;
const SMessageHeaderArea = styled.div `
  display: flex;
  align-items: center;
`;
const SAvatar = styled(Avatar) `
  &&& {
    width: 40px;
    height: 40px;
    margin-right: 12px;
  }
`;
const SUserName = styled.p `
  font-weight: bold;
  margin-bottom: 0;
  margin-right: 8px;

  &[data-usertype='worker'] {
    color: ${COLOR.ORANGE700};
  }
`;
const SDateTime = styled.p `
  margin-bottom: 0;
  margin-right: 16px;
  font-size: 13px;
`;
const SMessage = styled.p `
  margin-top: 8px;
  margin-left: 8px;
  margin-bottom: 4px;
  font-size: 14px;
`;
const SReadMark = styled.p `
  margin-left: 8px;
  color: ${COLOR.ORANGE700};
  font-size: 14px;
  margin-bottom: 0;
`;
const SSendIconArea = styled.div `
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 8px;
`;
const STextareaAutosize = styled(TextareaAutosize) `
  width: 100%;
  border: 1px solid ${COLOR.GRAY_300};
  border-radius: 6px;
`;
const SMessageImage = styled.img `
  width: auto;
  height: 150px;
  cursor: pointer;
`;
const SProgress = styled.div `
  text-align: center;
`;
const SUploadImage = styled.img `
  margin-right: auto;
  object-fit: cover;
  width: 25px;
  height: 25px;
  border-radius: 5px;
`;
const SPreviewImage = styled.div `
  width: 100vw;
  height: 100vh;
  position: relative;
  display: flex;
  border-top: 1px solid ${COLOR.GRAY_300};
`;
const SPreviewImageInner = styled.img `
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
const SClosePreviewButton = styled(IconButton) `
  && {
    position: absolute;
    top: 4px;
    right: 0;
  }
`;
const SClosePreviewTitle = styled.p `
  margin-bottom: 0px;
`;
